import type { ReactNode } from 'react';
import React from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import type {
  ComponentRenderProps,
  SectionProps,
} from '../../../types/dynamic_views';

interface SectionComponentProps extends ComponentRenderProps {
  children?: ReactNode;
}

// Build CSS classes
const variantClasses = {
  default: '',
  card: 'bg-bg-primary border border-border-secondary rounded-lg shadow-sm',
  bordered: 'border border-border-secondary rounded',
};

// Padding inside the section box. `default` variant has no visual chrome, so
// padding for it is pure breathing room — keep it small. `card`/`bordered`
// variants benefit from a bit more, but the defaults below were tuned for the
// research-brief layout where stacked sections were leaving ~72px of whitespace
// between adjacent content (16 bottom-padding + 24 margin + 16 top-padding +
// 16 header-margin). New defaults give ~28px of visual breathing room.
const paddingClasses = {
  none: 'p-0',
  sm: 'p-2',
  md: 'p-3',
  lg: 'p-6',
};

// Margin between adjacent sections. Halved from the original spacing because
// stacked sections inside one rendered brief were producing yawning gaps.
const spacingClasses = {
  tight: 'mb-2',
  normal: 'mb-3',
  loose: 'mb-6',
};

/**
 * Section is a layout container, not an interactive element. It deliberately
 * does NOT respond to `onInteraction` even when the parent renderer passes
 * one — the handler is for genuinely interactive components like
 * ConversationStarterComponent. The previous behavior (cursor-pointer +
 * hover:shadow + onClick whenever onInteraction was present) made every
 * section look clickable while emitting a generic 'click' that no consumer
 * actually handles. That's a UX lie — removed.
 */
export const SectionComponent: React.FC<SectionComponentProps> = ({
  component,
  className = '',
  children,
}) => {
  const { currentOrg } = useOrg();
  const props = component.props as SectionProps;
  const {
    title,
    content,
    variant = 'default',
    padding = 'md',
    spacing = 'normal',
  } = props;

  // Padding only applies when there's visual chrome to pad against. The
  // `default` variant has no border/background, so padding there is just
  // dead whitespace that pushes adjacent sections apart for no reason.
  // Card/bordered variants keep their padding scale.
  const appliesPadding = variant !== 'default';

  const allClasses = [
    variantClasses[variant] || variantClasses.default,
    appliesPadding ? paddingClasses[padding] || paddingClasses.md : '',
    spacingClasses[spacing] || spacingClasses.normal,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      className={allClasses}
      data-component-id={component.id}
      data-variant={variant}
    >
      {title && (
        <div className='section-header mb-2'>
          <h3 className='text-lg font-semibold text-fg-primary'>{title}</h3>
        </div>
      )}
      <div className='section-content'>
        {content && (
          <MarkdownContent
            content={content}
            orgId={currentOrg?.id}
            className='[&_p]:mb-2 [&_p:last-child]:mb-0'
          />
        )}
        {children}
      </div>
    </section>
  );
};

export default SectionComponent;
